import { useTranslation } from 'react-i18next'
import { Lock, Plus, X } from 'lucide-react'
import {
  COMPETITION_TYPES,
  PAYMENT_TIMING_LABELS,
  PAYMENT_TIMINGS,
  type CompetitionType,
  type PaymentTiming,
} from '@concours/shared'
import { Button, Input, Select, Textarea } from '@concours/ui'
import type { CompetitionForm } from '../../lib/competition-types'

export type FieldSection = 'info' | 'conditions' | 'calendar' | 'fees'

/** Section du formulaire qui porte chaque champ (pour rediriger vers l'étape en erreur). */
export const FIELD_SECTION: Record<string, FieldSection> = {
  reference: 'info',
  title: 'info',
  session: 'info',
  year: 'info',
  type: 'info',
  corps: 'info',
  grade: 'info',
  seats: 'info',
  applicationPrefix: 'info',
  summary: 'conditions',
  conditions: 'conditions',
  requiredDocuments: 'conditions',
  selectionRules: 'conditions',
  eligibility: 'conditions',
  'eligibility.minAge': 'conditions',
  'eligibility.maxAge': 'conditions',
  'eligibility.ageReferenceDate': 'conditions',
  publicationAt: 'calendar',
  opensAt: 'calendar',
  closesAt: 'calendar',
  paymentTiming: 'fees',
  feeAmount: 'fees',
  paymentDeadlineAt: 'fees',
}

interface Props {
  section: FieldSection
  form: CompetitionForm
  onChange: (patch: Partial<CompetitionForm>) => void
  errors?: Record<string, string>
  /** Champs figés (après publication) ; `*` = tout. */
  locked?: string[]
}

/** Champs d'un concours, par section — partagés par l'assistant et l'onglet « Modifier ». */
export function CompetitionFields({ section, form, onChange, errors = {}, locked = [] }: Props) {
  const { t } = useTranslation()
  const isLocked = (field: keyof CompetitionForm) => locked.includes('*') || locked.includes(field)
  const hint = (field: keyof CompetitionForm, text?: string) =>
    isLocked(field) ? (
      <span className="row" style={{ gap: 4 }}>
        <Lock aria-hidden="true" size={12} /> {t('competitions.fields.locked')}
      </span>
    ) : (
      text
    )
  const common = (field: keyof CompetitionForm) => ({
    disabled: isLocked(field),
    error: errors[field],
  })

  if (section === 'info')
    return (
      <div className="form-grid">
        <Input
          label={t('competitions.fields.title')}
          required
          value={form.title}
          onChange={(e) => onChange({ title: e.target.value })}
          {...common('title')}
          fieldClassName="span-2"
        />
        <Input
          label={t('competitions.fields.reference')}
          required
          mono
          value={form.reference}
          hint={hint('reference', t('competitions.fields.referenceHint'))}
          onChange={(e) => onChange({ reference: e.target.value.toUpperCase() })}
          {...common('reference')}
        />
        <Select
          label={t('competitions.fields.type')}
          value={form.type}
          hint={hint('type')}
          onChange={(e) => onChange({ type: e.target.value as CompetitionType })}
          options={Object.entries(COMPETITION_TYPES).map(([value, label]) => ({ value, label }))}
          {...common('type')}
        />
        <Input
          label={t('competitions.fields.session')}
          required
          value={form.session}
          onChange={(e) => onChange({ session: e.target.value })}
          {...common('session')}
        />
        <Input
          label={t('competitions.fields.year')}
          required
          type="number"
          min={2020}
          max={2100}
          value={form.year}
          hint={hint('year')}
          onChange={(e) => onChange({ year: e.target.value })}
          {...common('year')}
        />
        <Input
          label={t('competitions.fields.corps')}
          required
          value={form.corps}
          onChange={(e) => onChange({ corps: e.target.value })}
          {...common('corps')}
        />
        <Input
          label={t('competitions.fields.grade')}
          value={form.grade}
          onChange={(e) => onChange({ grade: e.target.value })}
          {...common('grade')}
        />
        <Input
          label={t('competitions.fields.seats')}
          required
          type="number"
          min={1}
          value={form.seats}
          onChange={(e) => onChange({ seats: e.target.value })}
          {...common('seats')}
        />
        <Input
          label={t('competitions.fields.prefix')}
          required
          mono
          maxLength={6}
          value={form.applicationPrefix}
          hint={hint(
            'applicationPrefix',
            t('competitions.fields.prefixHint', {
              example: `${form.applicationPrefix || 'PEN'}-${form.year || '2026'}-A7K9Q2`,
            })
          )}
          onChange={(e) =>
            onChange({ applicationPrefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })
          }
          {...common('applicationPrefix')}
        />
      </div>
    )

  if (section === 'conditions')
    return (
      <div className="stack">
        <Textarea
          label={t('competitions.fields.summary')}
          rows={3}
          value={form.summary}
          onChange={(e) => onChange({ summary: e.target.value })}
          {...common('summary')}
        />
        <Textarea
          label={t('competitions.fields.conditions')}
          hint={t('competitions.fields.conditionsHint')}
          rows={6}
          value={form.conditions}
          onChange={(e) => onChange({ conditions: e.target.value })}
          {...common('conditions')}
        />
        <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="field-label">{t('competitions.fields.ageTitle')}</legend>
          <p className="field-hint" style={{ marginBlockStart: 0 }}>
            {isLocked('eligibility' as keyof CompetitionForm)
              ? t('competitions.fields.locked')
              : t('competitions.fields.ageHint')}
          </p>
          <div className="grid-3">
            <Input
              label={t('competitions.fields.minAge')}
              type="number"
              min={14}
              max={70}
              value={form.minAge}
              disabled={isLocked('eligibility' as keyof CompetitionForm)}
              error={errors['eligibility.minAge']}
              onChange={(e) => onChange({ minAge: e.target.value })}
            />
            <Input
              label={t('competitions.fields.maxAge')}
              type="number"
              min={14}
              max={70}
              value={form.maxAge}
              disabled={isLocked('eligibility' as keyof CompetitionForm)}
              error={errors['eligibility.maxAge']}
              onChange={(e) => onChange({ maxAge: e.target.value })}
            />
            <Input
              label={t('competitions.fields.ageReferenceDate')}
              type="date"
              value={form.ageReferenceDate}
              disabled={isLocked('eligibility' as keyof CompetitionForm)}
              error={errors['eligibility.ageReferenceDate']}
              onChange={(e) => onChange({ ageReferenceDate: e.target.value })}
            />
          </div>
        </fieldset>
        <DocumentList
          value={form.requiredDocuments}
          onChange={(requiredDocuments) => onChange({ requiredDocuments })}
          disabled={isLocked('requiredDocuments')}
          error={errors.requiredDocuments}
        />
        <Textarea
          label={t('competitions.fields.selectionRules')}
          rows={4}
          value={form.selectionRules}
          onChange={(e) => onChange({ selectionRules: e.target.value })}
          {...common('selectionRules')}
        />
      </div>
    )

  if (section === 'calendar')
    return (
      <div className="form-grid">
        <Input
          label={t('competitions.fields.publicationAt')}
          type="datetime-local"
          value={form.publicationAt}
          hint={hint('publicationAt', t('competitions.fields.publicationHint'))}
          onChange={(e) => onChange({ publicationAt: e.target.value })}
          {...common('publicationAt')}
        />
        <span aria-hidden="true" />
        <Input
          label={t('competitions.fields.opensAt')}
          type="datetime-local"
          value={form.opensAt}
          hint={hint('opensAt')}
          onChange={(e) => onChange({ opensAt: e.target.value })}
          {...common('opensAt')}
        />
        <Input
          label={t('competitions.fields.closesAt')}
          type="datetime-local"
          value={form.closesAt}
          min={form.opensAt || undefined}
          hint={hint('closesAt', t('competitions.fields.closesHint'))}
          onChange={(e) => onChange({ closesAt: e.target.value })}
          {...common('closesAt')}
        />
      </div>
    )

  return (
    <div className="form-grid">
      <Select
        label={t('competitions.fields.paymentTiming')}
        value={form.paymentTiming}
        hint={hint('paymentTiming')}
        onChange={(e) => onChange({ paymentTiming: e.target.value as PaymentTiming })}
        options={PAYMENT_TIMINGS.map((value) => ({ value, label: PAYMENT_TIMING_LABELS[value] }))}
        {...common('paymentTiming')}
        fieldClassName="span-2"
      />
      {form.paymentTiming !== 'not_required' && (
        <>
          <Input
            label={t('competitions.fields.feeAmount')}
            required
            type="number"
            min={0}
            step={500}
            value={form.feeAmount}
            hint={hint('feeAmount', 'XOF')}
            onChange={(e) => onChange({ feeAmount: e.target.value })}
            {...common('feeAmount')}
          />
          <Input
            label={t('competitions.fields.paymentDeadlineAt')}
            type="datetime-local"
            value={form.paymentDeadlineAt}
            hint={hint('paymentDeadlineAt', t('competitions.fields.paymentDeadlineHint'))}
            onChange={(e) => onChange({ paymentDeadlineAt: e.target.value })}
            {...common('paymentDeadlineAt')}
          />
        </>
      )}
    </div>
  )
}

/** Liste éditable des pièces requises. */
function DocumentList({
  value,
  onChange,
  disabled,
  error,
}: {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  error?: string
}) {
  const { t } = useTranslation()
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }} disabled={disabled}>
      <legend className="field-label">{t('competitions.fields.requiredDocuments')}</legend>
      <div className="stack" style={{ gap: 'var(--space-2)' }}>
        {value.map((doc, index) => (
          <div key={index} className="row" style={{ gap: 'var(--space-2)' }}>
            <Input
              aria-label={t('competitions.fields.documentN', { n: index + 1 })}
              value={doc}
              fieldClassName="grow"
              onChange={(e) => onChange(value.map((d, i) => (i === index ? e.target.value : d)))}
            />
            <Button
              variant="ghost"
              iconOnly
              icon={<X />}
              aria-label={t('competitions.fields.removeDocument', { n: index + 1 })}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            />
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Plus />}
            onClick={() => onChange([...value, ''])}
          >
            {t('competitions.fields.addDocument')}
          </Button>
        </div>
        {error && <p className="field-error">{error}</p>}
      </div>
    </fieldset>
  )
}
